import { AppRoutes } from "@/constants/routes";
import { handlePermissionError } from "@/utils/permission-check";
import { toaster } from "@/components/ui/toaster";
import { getZonalGrossMarginList, GetZonalGrossMarginListProps, ZonalGrossMarginListResponse } from "@/services/zonal-gross-margin.service";
import { formatCurrencyInputToNumber } from "@/utils/query-string";
import { Dispatch, SetStateAction } from "react";
import { FieldValues } from "react-hook-form";

export const fetchZonalGrossMarginListHandler = async ({
  setLoading,
  setZonalGrossMarginList,
  showLoad = true,
  filters,
  navigate,
  fetchPermissions,
}: {
  setLoading: (loading: boolean) => void;
  setZonalGrossMarginList: (data: ZonalGrossMarginListResponse | null) => void;
  showLoad?: boolean;
  filters?: Partial<GetZonalGrossMarginListProps>;
  navigate: any;
  fetchPermissions: () => Promise<void>;
}) => {
  if (showLoad) setLoading(true);
  try {
    const params: GetZonalGrossMarginListProps = {
      "P.Pagina": filters?.["P.Pagina"] ?? 1,
      "P.Records": filters?.["P.Records"] ?? 10,
      ...(filters?.GranoProductivo && { GranoProductivo: filters.GranoProductivo }),
      ...(filters?.ZonaProductiva && { ZonaProductiva: filters.ZonaProductiva }),
      ...(filters?.CampanaProductiva && { CampanaProductiva: filters.CampanaProductiva }),
      ...(filters?.Equilibrio !== undefined && { Equilibrio: filters.Equilibrio }),
      ...(filters?.RindeEstimado !== undefined && { RindeEstimado: filters.RindeEstimado }),
    };
    const response = await getZonalGrossMarginList(params);
    setZonalGrossMarginList(response ?? null);
  } catch (error) {
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
      AppRoutes.ZONAL_GROSS_MARGINS,
    );

    if (!handled) {
      toaster.create({
        type: "error",
        title: "No se pudo cargar el listado de márgenes brutos zonales.",
      });
    }
    setZonalGrossMarginList(null);
  } finally {
    if (showLoad) setLoading(false);
  }
};

interface SubmitFiltersHandlerProps {
  setOpenFilters: Dispatch<SetStateAction<boolean>>;
  setFiltersApplied: Dispatch<SetStateAction<boolean>>;
  setCurrentFilters: Dispatch<SetStateAction<Partial<GetZonalGrossMarginListProps> | undefined>>;
  setPage: Dispatch<SetStateAction<number>>;
  fetchZonalGrossMarginList: (
    filters?: Partial<GetZonalGrossMarginListProps>,
    showLoad?: boolean,
  ) => Promise<void>;
  event: FieldValues;
}

export const submitFiltersHandler = async ({
  event,
  setOpenFilters,
  setFiltersApplied,
  setCurrentFilters,
  setPage,
  fetchZonalGrossMarginList,
}: SubmitFiltersHandlerProps) => {
  setFiltersApplied(true);

  const filterObj: Partial<GetZonalGrossMarginListProps> = {};

  if (event.GranoProductivo && event.GranoProductivo.length > 0) {
    filterObj.GranoProductivo = event.GranoProductivo;
  }
  if (event.ZonaProductiva && event.ZonaProductiva.length > 0) {
    filterObj.ZonaProductiva = event.ZonaProductiva;
  }
  if (event.CampanaProductiva && event.CampanaProductiva.length > 0) {
    filterObj.CampanaProductiva = event.CampanaProductiva;
  }
  if (event.Equilibrio !== undefined && event.Equilibrio !== "") {
    filterObj.Equilibrio = formatCurrencyInputToNumber(event.Equilibrio);
  }
  if (event.RindeEstimado !== undefined && event.RindeEstimado !== "") {
    filterObj.RindeEstimado = formatCurrencyInputToNumber(event.RindeEstimado);
  }

  setCurrentFilters(filterObj);
  if (!Object.keys(filterObj).length) setFiltersApplied(false);

  setPage(1);
  await fetchZonalGrossMarginList({ ...filterObj, "P.Pagina": 1 }, true);
  setOpenFilters(false);
};

interface ClearFiltersHandlerProps {
  setValue: any;
  setFiltersApplied: Dispatch<SetStateAction<boolean>>;
  getValues: any;
  setOpenFilters: Dispatch<SetStateAction<boolean>>;
  submitFilters: (event: FieldValues) => Promise<void>;
}

export const clearFiltersHandler = async ({
  setValue,
  setFiltersApplied,
  getValues,
  setOpenFilters,
  submitFilters,
}: ClearFiltersHandlerProps) => {
  setValue("GranoProductivo", []);
  setValue("ZonaProductiva", []);
  setValue("CampanaProductiva", []);
  setValue("Equilibrio", "");
  setValue("RindeEstimado", "");

  setFiltersApplied(false);
  setOpenFilters(false);
  await submitFilters(getValues());
};
